/**
 * UserProfile component for displaying user information and profile photo
 * Uses Microsoft Graph API to fetch and display user profile data
 */

import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { makeStyles, tokens, Spinner } from '@fluentui/react-components';
import { graphTokenRequest } from '../auth/authConfig';
import { getCompleteUserProfile, GraphUser } from '../utils/graphApi';

interface UserProfileProps {
  className?: string;
  showFullProfile?: boolean; // If true, shows full profile; if false, shows compact version
  size?: 'small' | 'medium' | 'large'; // Photo size
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  photoContainer: {
    borderRadius: '50%',
    overflow: 'hidden',
    backgroundColor: tokens.colorBrandBackground,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  photoSmall: {
    width: '32px',
    height: '32px',
  },
  photoMedium: {
    width: '48px',
    height: '48px',
  },
  photoLarge: {
    width: '64px',
    height: '64px',
  },
  photo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  initials: {
    color: tokens.colorNeutralForegroundOnBrand,
    fontWeight: tokens.fontWeightMedium,
    userSelect: 'none',
  },
  initialsSmall: {
    fontSize: '12px',
  },
  initialsMedium: {
    fontSize: '16px',
  },
  initialsLarge: {
    fontSize: '20px',
  },
  profileInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  displayName: {
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
  },
  jobTitle: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  email: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  department: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  errorContainer: {
    backgroundColor: tokens.colorPaletteRedBackground1,
    color: tokens.colorPaletteRedForeground1,
  },
  loadingContainer: {
    backgroundColor: tokens.colorNeutralBackground2,
  },
  errorIcon: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: '12px',
  },
  unknownIcon: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
  },
});

export const UserProfile: React.FC<UserProfileProps> = ({ 
  className = '', 
  showFullProfile = false,
  size = 'medium' 
}) => {
  const { instance, accounts } = useMsal();
  const [userProfile, setUserProfile] = useState<GraphUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState(false);
  const classes = useStyles();

  const photoSizeClass = {
    small: classes.photoSmall,
    medium: classes.photoMedium,
    large: classes.photoLarge
  }[size];

  const initialsClass = {
    small: classes.initialsSmall,
    medium: classes.initialsMedium,
    large: classes.initialsLarge
  }[size];

  useEffect(() => {
    if (accounts && accounts.length > 0) {
      fetchUserProfile();
    }
  }, [accounts]);

  // Reset photo error when user profile changes
  useEffect(() => {
    setPhotoError(false);
  }, [userProfile?.photo]);

  const fetchUserProfile = async () => {
    if (!accounts || accounts.length === 0) {
      setError('No user account found');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Request access token for Graph API
      const request = {
        scopes: graphTokenRequest.scopes, // Use Graph-specific token request
        account: accounts[0],
      };

      const response = await instance.acquireTokenSilent(request);
      
      if (response.accessToken) {
        const profile = await getCompleteUserProfile(response.accessToken);
        setUserProfile(profile);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      
      if (error instanceof InteractionRequiredAuthError) {
        // If silent token acquisition fails, try interactive
        try {
          const request = {
            scopes: graphTokenRequest.scopes, // Use Graph-specific token request
            account: accounts[0],
          };
          const response = await instance.acquireTokenPopup(request);
          
          if (response.accessToken) {
            const profile = await getCompleteUserProfile(response.accessToken);
            setUserProfile(profile);
          }
        } catch (interactiveError) {
          console.error('Interactive token acquisition failed:', interactiveError);
          setError('Failed to get user permissions for profile data');
        }
      } else {
        setError('Failed to load user profile');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`${classes.container} ${className}`}>
        <div className={`${classes.photoContainer} ${photoSizeClass} ${classes.loadingContainer}`}>
          <Spinner size="tiny" />
        </div>
        {showFullProfile && (
          <div className={classes.profileInfo}>
            <div style={{ color: tokens.colorNeutralForeground2, fontSize: tokens.fontSizeBase200 }}>
              Loading profile...
            </div>
          </div>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${classes.container} ${className}`}>
        <div className={`${classes.photoContainer} ${photoSizeClass} ${classes.errorContainer}`}>
          <span className={classes.errorIcon}>!</span>
        </div>
        {showFullProfile && (
          <div className={classes.profileInfo}>
            <div style={{ color: tokens.colorPaletteRedForeground1, fontSize: tokens.fontSizeBase200 }}>
              {error}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className={`${classes.container} ${className}`}>
        <div className={`${classes.photoContainer} ${photoSizeClass}`} style={{ backgroundColor: tokens.colorNeutralBackground3 }}>
          <span className={classes.unknownIcon}>?</span>
        </div>
        {showFullProfile && (
          <div className={classes.profileInfo}>
            <div style={{ color: tokens.colorNeutralForeground2, fontSize: tokens.fontSizeBase200 }}>
              No profile data
            </div>
          </div>
        )}
      </div>
    );
  }

  const displayName = userProfile.displayName || userProfile.userPrincipalName || 'Unknown User';
  const initials = displayName
    .split(' ')
    .map(name => name.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 2);

  // Only show initials if no photo URL or if photo failed to load
  const shouldShowInitials = !userProfile.photo || photoError;

  return (
    <div className={`${classes.container} ${className}`}>
      {/* Profile Photo or Initials */}
      <div className={`${classes.photoContainer} ${photoSizeClass}`}>
        {userProfile.photo && !photoError ? (
          <img
            src={userProfile.photo}
            alt={`${displayName}'s profile`}
            className={classes.photo}
            onError={() => {
              // If image fails to load, switch to initials
              setPhotoError(true);
            }}
          />
        ) : shouldShowInitials ? (
          <div className={`${classes.initials} ${initialsClass}`}>
            {initials}
          </div>
        ) : null}
      </div>

      {/* Profile Information */}
      {showFullProfile && (
        <div className={classes.profileInfo}>
          <div className={classes.displayName}>
            {displayName}
          </div>
          {userProfile.jobTitle && (
            <div className={classes.jobTitle}>
              {userProfile.jobTitle}
            </div>
          )}
          {userProfile.mail && (
            <div className={classes.email}>
              {userProfile.mail}
            </div>
          )}
          {userProfile.department && (
            <div className={classes.department}>
              {userProfile.department}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
